import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState(null);
  const [connectionMethod, setConnectionMethod] = useState(null);
  const [error, setError] = useState(null);

  const checkExistingConnection = async () => {
    // DO NOT AUTO-RESTORE CONNECTIONS ON PAGE LOAD
    // User must explicitly click connect button
    console.log('Skipping automatic connection restoration - user must click connect');
    
    // Clear any stored connection data to prevent auto-connection
    localStorage.removeItem('connectionMethod');
    localStorage.removeItem('connectedAddress');
  };

  const disconnectWallet = useCallback(async () => {
    try {
      setWallet(null);
      setConnectionMethod(null);
      setError(null);
      
      // Clear storage
      localStorage.removeItem('connectionMethod');
      localStorage.removeItem('connectedAddress');
      
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      // Force disconnect even if there's an error
      setWallet(null);
      setConnectionMethod(null);
      localStorage.removeItem('connectionMethod');
      localStorage.removeItem('connectedAddress');
    }
  }, []);

  const updateWalletInfo = useCallback((address, chainId) => {
    if (wallet && address) {
      setWallet(prev => ({
        ...prev,
        address,
        chainId: chainId || prev.chainId
      }));
      
      // Update stored address if it changed
      if (address !== localStorage.getItem('connectedAddress')) {
        localStorage.setItem('connectedAddress', address);
      }
    }
  }, [wallet]);

  const switchNetwork = async (chainId) => {
    try {
      if (connectionMethod === 'rainbowkit') {
        // Network switching will be handled by RainbowKit
        updateWalletInfo(wallet.address, chainId);
        return { success: true };
      } else {
        throw new Error('No wallet connected');
      }
    } catch (error) {
      console.error('Network switch failed:', error);
      return { success: false, error: error.message };
    }
  };

  const signMessage = async (message) => {
    try {
      if (connectionMethod === 'rainbowkit') {
        // Message signing will be handled by RainbowKit
        return { success: true, message: 'Message signing handled by RainbowKit' };
      } else {
        throw new Error('No wallet connected');
      }
    } catch (error) {
      console.error('Message signing failed:', error);
      return { success: false, error: error.message };
    }
  };

  const getBalance = async () => {
    try {
      if (connectionMethod === 'rainbowkit') {
        // Balance will be handled by RainbowKit
        return "0";
      } else {
        throw new Error('No wallet connected');
      }
    } catch (error) {
      console.error('Failed to get balance:', error);
      return "0";
    }
  };

  useEffect(() => {
    // Check for existing connection on mount
    checkExistingConnection();
  }, []);

  const value = {
    wallet,
    connectionMethod,
    error,
    disconnectWallet,
    switchNetwork,
    signMessage,
    getBalance,
    updateWalletInfo
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}; 